export interface DeploymentMessage {
  type: "deployment";
  buildId: string;
}

export class ServiceWorkerHelper {
  private static registration: ServiceWorkerRegistration | null = null;

  /**
   * Registers the service worker and reports the build it serves to "onDeployment".
   *
   * Attention: Do not reload the page on "controllerchange". That event fires in every
   * open tab as soon as a new service worker claims them - including the tabs that were
   * opened after the deployment and already run the newest build. It also fires only
   * once the (slow) precaching of the new service worker is done, which is why it used
   * to hit the user as an unexplained reload minutes after the page had loaded. The
   * build id reported here lets the caller reload only the pages that really are stale.
   */
  public static async registerServiceWorker(onDeployment: (buildId: string) => void): Promise<void> {
    console.info("Register service worker");

    navigator.serviceWorker.addEventListener("message", event => {
      const message = event.data as DeploymentMessage | undefined;

      if (message && message.type === "deployment" && message.buildId) {
        onDeployment(message.buildId);
      }
    });

    // updateViaCache "none": never serve service-worker.js or its imported polyfills
    // from the HTTP cache of the browser during update checks
    ServiceWorkerHelper.registration = await navigator.serviceWorker.register(ServiceWorkerHelper.getScriptUrl(), {
      updateViaCache: "none",
    });

    ServiceWorkerHelper.askForBuild();
  }

  /**
   * The browser checks for a new service-worker.js only on full page loads, which are
   * rare in a long-lived SPA tab. Call this to trigger an explicit update check, so that
   * the new build is precached (and therefore available offline) as early as possible.
   */
  public static async checkForUpdate(): Promise<void> {
    if (ServiceWorkerHelper.registration) {
      await ServiceWorkerHelper.registration.update();
    }
  }

  /**
   * The build ships every bundle twice, modern and legacy, and a browser only ever runs one
   * of the two sets. The service worker precaches only the set named here, otherwise every
   * browser would download both. A browser keeps running the same set, so the url stays the
   * same from one page load to the next and does not cause extra service worker updates.
   */
  private static getScriptUrl(): string {
    return "/service-worker.js?bundle=" + (__MODERN_BUNDLE__ ? "modern" : "legacy");
  }

  /**
   * An already activated service worker announces its build only once, long before this
   * page existed. Ask it again, otherwise a page that was restored from the back/forward
   * cache would never learn which build is served.
   */
  private static askForBuild(): void {
    navigator.serviceWorker.controller?.postMessage({ type: "get-build" });
  }
}
